/**
 * TUVI - Adicionales de Proyecto
 * Permite crear, editar, eliminar y descargar PDF de adicionales
 * Usa la misma matriz de actividades del cotizador
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import { UPLOAD_URL } from '../../../config';
import client from '../../../services/graphqlClient';
import AdicionalPDF from './AdicionalPDF';
import '../../Documentos/Cotizaciones.css';

// Construye la URL para ver el comprobante (vive en MinIO, bucket "comprobantes",
// servido por el VPS nuevo). Codifica espacios/acentos pero conserva los "/".
const comprobanteUrl = (key) =>
  key ? `${UPLOAD_URL}/adicional-comprobante/${key.split('/').map(encodeURIComponent).join('/')}` : null;

const ORDEN_CATEGORIAS = [
  'PRELIMINARES', 'MUROS', 'REDES ELECTRICAS', 'PISOS',
  'ENCHAPES Y REVOQUE', 'ESTUCO, PINTURA Y DRYWALL',
  'CARPINTERIA EN MADERA', 'MESONES', 'APARATOS SANITARIOS Y GRIFERIAS',
  'ELECTRODOMESTICOS', 'VIDRIOS', 'ACTIVIDADES ESPECIALES',
  'CARPINTERIA ESPECIAL', 'FINALES'
];

const formatCurrency = (value) => {
  if (!value && value !== 0) return '$0';
  return new Intl.NumberFormat('es-CO', {
    style: 'currency', currency: 'COP',
    minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(value);
};

const AdicionalesProyecto = ({ proyectoId, proyecto, onProyectoActualizado }) => {
  const { success, error: showError } = useNotification();

  // Vista: 'lista', 'formulario' o 'historial'
  const [vista, setVista] = useState('lista');
  const [adicionales, setAdicionales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);

  // Formulario
  const [editandoId, setEditandoId] = useState(null);
  const [tipoAdicional, setTipoAdicional] = useState('adicional'); // 'adicional' | 'retiro'
  const [descripcion, setDescripcion] = useState('');
  const [diasAdicionales, setDiasAdicionales] = useState(0);
  const [incrementoGlobal, setIncrementoGlobal] = useState(0);
  const [notas, setNotas] = useState('');
  const [actividadesSeleccionadas, setActividadesSeleccionadas] = useState([]);
  const cargandoAdicionalRef = useRef(false);
  const prevIncGlobalRef = useRef(0);

  // Aprobacion
  const [modalAprobacion, setModalAprobacion] = useState(null); // adicional object or null
  const [archivoComprobante, setArchivoComprobante] = useState(null);
  const [aprobando, setAprobando] = useState(false);
  const fileInputRef = useRef(null);

  // Historial
  const [historial, setHistorial] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);

  // Selector de actividades (adicional - desde matriz)
  const [categoriaActiva, setCategoriaActiva] = useState('');
  const [espacioActivo, setEspacioActivo] = useState('');
  const [buscarActividad, setBuscarActividad] = useState('');
  const [espaciosDisponibles, setEspaciosDisponibles] = useState([]);
  const [actividadesDisponibles, setActividadesDisponibles] = useState([]);
  const [loadingActividades, setLoadingActividades] = useState(false);

  // Selector de actividades (retiro - desde proyecto)
  const [actividadesProyecto, setActividadesProyecto] = useState([]);
  const [buscarRetiro, setBuscarRetiro] = useState('');
  const [loadingProyecto, setLoadingProyecto] = useState(false);

  // PDF
  const [adicionalPDF, setAdicionalPDF] = useState(null);
  const [generandoPDF, setGenerandoPDF] = useState(false);
  const pdfContainerRef = useRef(null);

  // Totales calculados
  const calcularTotales = useCallback(() => {
    const subtotal = actividadesSeleccionadas.reduce((sum, act) => sum + (parseFloat(act.valor_total) || 0), 0);
    const aiu_valor = subtotal * 0.15;
    const iva_valor = subtotal * 0.19 * 0.05;
    const valor_total = subtotal + aiu_valor + iva_valor;
    const valor_proyecto_anterior = parseFloat(proyecto?.proyecto?.valor_final) || 0;
    const valor_proyecto_actualizado = tipoAdicional === 'retiro'
      ? valor_proyecto_anterior - valor_total
      : valor_proyecto_anterior + valor_total;

    return { subtotal, aiu_valor, iva_valor, valor_total, valor_proyecto_anterior, valor_proyecto_actualizado };
  }, [actividadesSeleccionadas, proyecto, tipoAdicional]);

  const totales = calcularTotales();

  // Cargar adicionales del proyecto (migrado a GraphQL)
  const cargarAdicionales = async () => {
    try {
      setLoading(true);
      const data = await client.query(`query($pid: Int!) { adicionalesProyecto(proyecto_id: $pid) { id proyecto_id consecutivo fecha descripcion tipo subtotal aiu_valor iva_valor valor_total valor_proyecto_anterior valor_proyecto_actualizado estado notas comprobante_archivo fecha_aprobacion dias_adicionales actividades { id actividad_id codigo descripcion capitulo categoria espacio unidad cantidad valor_unitario incremento factor_desperdicio factor_multiplicador valor_total } } }`, { pid: proyectoId });
      setAdicionales(data.adicionalesProyecto || []);
    } catch (err) {
      console.error('Error cargando adicionales:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarAdicionales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proyectoId]);

  // Cargar actividades del proyecto (para retiros) - migrado a GraphQL
  const cargarActividadesProyecto = useCallback(async (buscar = '') => {
    setLoadingProyecto(true);
    try {
      const data = await client.query(`query($pid: Int!, $b: String) { actividadesProyectoParaRetiro(proyecto_id: $pid, buscar: $b) { actividad_id codigo descripcion capitulo categoria espacio unidad cantidad valor_unitario incremento factor_desperdicio factor_multiplicador valor_total } }`, { pid: proyectoId, b: buscar || null });
      setActividadesProyecto(data.actividadesProyectoParaRetiro || []);
    } catch (err) {
      console.error('Error cargando actividades del proyecto:', err);
    } finally {
      setLoadingProyecto(false);
    }
  }, [proyectoId]);

  // Auto-cargar actividades del proyecto cuando cambia a retiro en formulario
  useEffect(() => {
    if (tipoAdicional === 'retiro' && vista === 'formulario' && actividadesProyecto.length === 0) {
      cargarActividadesProyecto('');
    }
  }, [tipoAdicional, vista, cargarActividadesProyecto, actividadesProyecto.length]);

  // Cargar historial de aprobaciones - migrado a GraphQL
  const cargarHistorial = async () => {
    try {
      setLoadingHistorial(true);
      const data = await client.query(`query($pid: Int!) { adicionalesHistorial(proyecto_id: $pid) { id adicional_id consecutivo descripcion tipo valor_adicional valor_proyecto_antes valor_proyecto_despues comprobante_archivo fecha_aprobacion } }`, { pid: proyectoId });
      setHistorial(data.adicionalesHistorial || []);
    } catch (err) {
      console.error('Error cargando historial:', err);
    } finally {
      setLoadingHistorial(false);
    }
  };

  // Aprobar adicional con comprobante
  const aprobarAdicional = async () => {
    if (!archivoComprobante) {
      showError('Debe adjuntar un comprobante de aprobacion');
      return;
    }

    setAprobando(true);
    try {
      // Subir comprobante a MinIO si existe
      let comprobanteKey = null;
      if (archivoComprobante) {
        const fd = new FormData();
        fd.append('proyecto', proyecto?.proyecto?.proyecto_nombre || '');
        fd.append('apartamento', proyecto?.proyecto?.apartamento || '');
        fd.append('files', archivoComprobante);
        const token = localStorage.getItem('tuvi_token');
        const { UPLOAD_URL } = await import('../../../config');
        const upRes = await fetch(`${UPLOAD_URL}/catalogo/upload?prefix=adicionales/&bucket=comprobantes`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd });
        const upData = await upRes.json();
        if (upData.files?.[0]?.key) comprobanteKey = upData.files[0].key;
      }

      const gqlR = await client.mutate(`mutation($id: Int!, $key: String) { aprobarAdicional(id: $id, comprobante_key: $key) { success message valor_proyecto_actualizado } }`, { id: modalAprobacion.id, key: comprobanteKey });
      const data = gqlR.aprobarAdicional;
      if (data.success) {
        const tipoLabel = modalAprobacion.tipo === 'retiro' ? 'Retiro' : 'Adicional';
        // El backend (aprobarAdicional) ya actualiza valor + días (plazo, fecha de
        // entrega y cronograma) de forma atómica. No se llama nada extra aquí.
        success(`${tipoLabel} aprobado. Valor del proyecto actualizado a ${formatCurrency(data.valor_proyecto_actualizado)}`);

        setModalAprobacion(null);
        setArchivoComprobante(null);
        cargarAdicionales();
        if (onProyectoActualizado) onProyectoActualizado();
      } else {
        showError(data.message || 'Error al aprobar');
      }
    } catch (err) {
      showError('Error al aprobar adicional');
      console.error(err);
    } finally {
      setAprobando(false);
    }
  };

  const abrirModalAprobacion = (adicional) => {
    setModalAprobacion(adicional);
    setArchivoComprobante(null);
  };

  const verHistorial = () => {
    cargarHistorial();
    setVista('historial');
  };

  // Cargar espacios cuando cambia la categoría
  useEffect(() => {
    if (!categoriaActiva) {
      setEspaciosDisponibles([]);
      setEspacioActivo('');
      setActividadesDisponibles([]);
      return;
    }
    const cargarEspacios = async () => {
      try {
        const data = await client.query(`query($cat: String!) { adicionalesEspacios(categoria: $cat) }`, { cat: categoriaActiva });
        const espacios = data.adicionalesEspacios || [];
        setEspaciosDisponibles(espacios);
        if (espacios.length === 1) setEspacioActivo(espacios[0]);
      } catch (err) {
        console.error('Error cargando espacios:', err);
      }
    };
    cargarEspacios();
    setEspacioActivo('');
    setActividadesDisponibles([]);
  }, [categoriaActiva]);

  // Cargar actividades cuando cambia espacio o búsqueda
  const actividadesAbortRef = useRef(null);
  useEffect(() => {
    if (!categoriaActiva || !espacioActivo) return;

    const timeout = setTimeout(async () => {
      if (actividadesAbortRef.current) actividadesAbortRef.current.abort();
      const controller = new AbortController();
      actividadesAbortRef.current = controller;

      try {
        setLoadingActividades(true);
        const data = await client.query(`query($cat: String, $esp: String, $b: String) { adicionalesActividades(categoria: $cat, espacio: $esp, buscar: $b) { id actividad_id codigo descripcion capitulo categoria espacio unidad cantidad valor_unitario incremento factor_desperdicio factor_multiplicador } }`, { cat: categoriaActiva, esp: espacioActivo, b: buscarActividad || null });
        if (data.adicionalesActividades) {
          const ordenadas = (data.adicionalesActividades || []).sort((a, b) => {
            const vfA = calcularValorFinalActividad(a);
            const vfB = calcularValorFinalActividad(b);
            return vfA - vfB;
          });
          setActividadesDisponibles(ordenadas);
        }
      } catch (err) {
        if (err.name !== 'AbortError') console.error('Error cargando actividades:', err);
      } finally {
        setLoadingActividades(false);
      }
    }, 400);

    return () => clearTimeout(timeout);
  }, [categoriaActiva, espacioActivo, buscarActividad]);

  const calcularValorFinalActividad = (act) => {
    const vu = parseFloat(act.valor_unitario) || 0;
    const inc = parseFloat(act.incremento_actividades || act.incremento) || 0;
    const desp = parseFloat(act.factor_desperdicio) || 1;
    const mult = parseFloat(act.factor_multiplicador) || 1;
    return vu * (1 + inc / 100) * desp * mult;
  };

  const agregarActividad = (actividad) => {
    const yaExiste = actividadesSeleccionadas.some(a => a.actividad_id === actividad.id);
    if (yaExiste) {
      showError('Esta actividad ya esta agregada');
      return;
    }

    const vuBase = parseFloat(actividad.valor_unitario) || 0;
    const inc = parseFloat(actividad.incremento_actividades) || 0;
    const desp = parseFloat(actividad.factor_desperdicio) || 1;
    const mult = parseFloat(actividad.factor_multiplicador) || 1;
    const cantidad = parseFloat(actividad.cantidad_default) || 1;
    // Aplicar el incremento global actual al valor_unitario mostrado
    const incG = Number(incrementoGlobal) || 0;
    const vuMostrado = Math.floor(vuBase * (1 + incG / 100));
    const valorTotal = Math.floor(vuMostrado * (1 + inc / 100) * desp * mult) * cantidad;

    setActividadesSeleccionadas(prev => [...prev, {
      actividad_id: actividad.id,
      codigo: actividad.codigo,
      descripcion: actividad.descripcion || actividad.actividad,
      capitulo: actividad.capitulo,
      categoria: actividad.categoria,
      espacio: actividad.espacio,
      unidad: actividad.unidad,
      cantidad,
      valor_unitario_base: vuBase,
      valor_unitario: vuMostrado,
      incremento: inc,
      factor_desperdicio: desp,
      factor_multiplicador: mult,
      valor_total: valorTotal
    }]);
    success('Actividad agregada');
  };

  // Agregar actividad del proyecto para retiro
  const agregarActividadRetiro = (actividad) => {
    const yaExiste = actividadesSeleccionadas.some(a => a.actividad_id === actividad.id);
    if (yaExiste) {
      showError('Esta actividad ya esta agregada');
      return;
    }

    setActividadesSeleccionadas(prev => [...prev, {
      actividad_id: actividad.actividad_id,
      codigo: actividad.codigo || '',
      descripcion: actividad.descripcion,
      capitulo: actividad.capitulo || '',
      categoria: actividad.categoria || '',
      espacio: actividad.espacio || '',
      unidad: actividad.unidad || 'GL',
      cantidad: Number(actividad.cantidad) || 1,
      valor_unitario: Number(actividad.valor_unitario || actividad.valor_total) || 0,
      valor_unitario_base: Number(actividad.valor_unitario || actividad.valor_total) || 0,
      incremento: Number(actividad.incremento) || 0,
      factor_desperdicio: Number(actividad.factor_desperdicio) || 1,
      factor_multiplicador: Number(actividad.factor_multiplicador) || 1,
      valor_total: actividad.valor_total || 0
    }]);
    success('Actividad agregada al retiro');
  };

  // Campos que deben enviarse como Float al backend
  const CAMPOS_NUMERICOS = ['cantidad', 'valor_unitario', 'incremento', 'factor_desperdicio', 'factor_multiplicador', 'valor_total', 'valor_unitario_base'];

  const actualizarActividad = (index, campo, valor) => {
    setActividadesSeleccionadas(prev => {
      const nuevas = [...prev];
      // Para campos numéricos: convertir a Number (vacío → 0)
      const nuevoValor = CAMPOS_NUMERICOS.includes(campo)
        ? (valor === '' || valor === null || valor === undefined ? 0 : Number(valor))
        : valor;
      nuevas[index] = { ...nuevas[index], [campo]: nuevoValor };

      if (campo === 'valor_unitario' || campo === 'cantidad') {
        const vu = Number(nuevas[index].valor_unitario) || 0;
        const inc = Number(nuevas[index].incremento) || 0;
        const desp = Number(nuevas[index].factor_desperdicio) || 1;
        const mult = Number(nuevas[index].factor_multiplicador) || 1;
        const cantidad = Number(nuevas[index].cantidad) || 0;
        nuevas[index].valor_total = Math.floor(vu * (1 + inc / 100) * desp * mult) * cantidad;
        // Si el usuario edita valor_unitario manualmente, sincronizar la base para que
        // el incremento global se aplique sobre el nuevo valor.
        if (campo === 'valor_unitario') {
          const incG = Number(prevIncGlobalRef.current) || 0;
          nuevas[index].valor_unitario_base = incG !== 0
            ? Math.floor(vu / (1 + incG / 100))
            : vu;
        }
      }

      return nuevas;
    });
  };

  // Recalcular valor_unitario y valor_total cuando cambia el Incremento Global (%)
  useEffect(() => {
    if (cargandoAdicionalRef.current) {
      prevIncGlobalRef.current = Number(incrementoGlobal) || 0;
      return;
    }
    const incG = Number(incrementoGlobal) || 0;
    if (incG === prevIncGlobalRef.current) return;
    prevIncGlobalRef.current = incG;

    setActividadesSeleccionadas(prev => {
      if (prev.length === 0) return prev;
      return prev.map(act => {
        const base = Number(act.valor_unitario_base) || Number(act.valor_unitario) || 0;
        const inc = Number(act.incremento) || 0;
        const desp = Number(act.factor_desperdicio) || 1;
        const mult = Number(act.factor_multiplicador) || 1;
        const cantidad = Number(act.cantidad) || 0;
        const vuMostrado = Math.floor(base * (1 + incG / 100));
        const valorTotal = Math.floor(vuMostrado * (1 + inc / 100) * desp * mult) * cantidad;
        return { ...act, valor_unitario_base: base, valor_unitario: vuMostrado, valor_total: valorTotal };
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incrementoGlobal]);

  const eliminarActividad = (index) => {
    setActividadesSeleccionadas(prev => prev.filter((_, i) => i !== index));
  };

  // Guardar adicional
  const guardarAdicional = async () => {
    if (actividadesSeleccionadas.length === 0) {
      showError('Debe agregar al menos una actividad');
      return;
    }

    setGuardando(true);
    try {
      // Sanitizar actividades: asegurar que todos los campos numéricos sean Number
      const actividadesSanit = actividadesSeleccionadas.map(a => ({
        actividad_id: a.actividad_id || null,
        codigo: a.codigo || '',
        descripcion: a.descripcion || '',
        capitulo: a.capitulo || '',
        categoria: a.categoria || '',
        espacio: a.espacio || '',
        unidad: a.unidad || '',
        cantidad: Number(a.cantidad) || 0,
        valor_unitario: Number(a.valor_unitario) || 0,
        valor_unitario_base: a.valor_unitario_base != null ? Number(a.valor_unitario_base) : null,
        incremento: Number(a.incremento) || 0,
        factor_desperdicio: Number(a.factor_desperdicio) || 1,
        factor_multiplicador: Number(a.factor_multiplicador) || 1,
        valor_total: Number(a.valor_total) || 0,
      }));

      const payload = {
        proyecto_id: proyectoId,
        fecha: new Date().toISOString().split('T')[0],
        tipo: tipoAdicional,
        descripcion,
        subtotal: totales.subtotal,
        aiu_valor: totales.aiu_valor,
        iva_valor: totales.iva_valor,
        valor_total: totales.valor_total,
        valor_proyecto_anterior: totales.valor_proyecto_anterior,
        valor_proyecto_actualizado: totales.valor_proyecto_actualizado,
        notas,
        dias_adicionales: parseInt(diasAdicionales) || 0,
        // El incremento solo aplica a adicionales; en retiro siempre 0.
        incremento_global: tipoAdicional === 'retiro' ? 0 : (Number(incrementoGlobal) || 0),
        actividades: actividadesSanit,
      };

      let data;
      if (editandoId) {
        const gqlR = await client.mutate(`mutation($id: Int!, $input: AdicionalInput!) { editarAdicional(id: $id, input: $input) { success message } }`, { id: editandoId, input: payload });
        data = gqlR.editarAdicional;
      } else {
        const gqlR = await client.mutate(`mutation($input: AdicionalInput!) { crearAdicional(input: $input) { success message id } }`, { input: payload });
        data = gqlR.crearAdicional;
      }
      if (data.success) {
        const tipoLabel = tipoAdicional === 'retiro' ? 'Retiro' : 'Adicional';
        success(editandoId ? `${tipoLabel} actualizado` : `${tipoLabel} creado`);
        resetFormulario();
        setVista('lista');
        cargarAdicionales();
      } else {
        showError(data.message || 'Error al guardar');
      }
    } catch (err) {
      showError('Error al guardar adicional');
      console.error(err);
    } finally {
      setGuardando(false);
    }
  };

  // Editar adicional existente - migrado a GraphQL
  const editarAdicional = async (id) => {
    try {
      const gqlR = await client.query(`query($id: Int!) { adicionalDetalle(id: $id) { id tipo descripcion notas dias_adicionales incremento_global actividades { actividad_id codigo descripcion capitulo categoria espacio unidad cantidad valor_unitario valor_unitario_base incremento factor_desperdicio factor_multiplicador valor_total } } }`, { id });
      if (gqlR.adicionalDetalle) {
        const adic = gqlR.adicionalDetalle;
        cargandoAdicionalRef.current = true; // bloquea el efecto de recálculo
        setEditandoId(adic.id);
        setTipoAdicional(adic.tipo || 'adicional');
        setDescripcion(adic.descripcion || '');
        setNotas(adic.notas || '');
        setDiasAdicionales(parseInt(adic.dias_adicionales) || 0);
        setIncrementoGlobal(parseFloat(adic.incremento_global) || 0);
        prevIncGlobalRef.current = parseFloat(adic.incremento_global) || 0;
        setActividadesSeleccionadas((adic.actividades || []).map(act => ({
          actividad_id: act.actividad_id,
          codigo: act.codigo,
          descripcion: act.descripcion,
          capitulo: act.capitulo,
          categoria: act.categoria,
          espacio: act.espacio,
          unidad: act.unidad,
          cantidad: parseFloat(act.cantidad) || 1,
          valor_unitario: parseFloat(act.valor_unitario) || 0,
          valor_unitario_base: act.valor_unitario_base != null
            ? parseFloat(act.valor_unitario_base)
            : (parseFloat(act.valor_unitario) || 0),
          incremento: parseFloat(act.incremento) || 0,
          factor_desperdicio: parseFloat(act.factor_desperdicio) || 1,
          factor_multiplicador: parseFloat(act.factor_multiplicador) || 1,
          valor_total: parseFloat(act.valor_total) || 0
        })));
        setVista('formulario');
        // Liberar el bloqueo después de que React aplique los setState
        setTimeout(() => { cargandoAdicionalRef.current = false; }, 100);
      }
    } catch (err) {
      showError('Error al cargar adicional');
      console.error(err);
    }
  };

  // Eliminar adicional - migrado a GraphQL
  const eliminarAdicional = async (id) => {
    if (!window.confirm('¿Eliminar este adicional?')) return;
    try {
      const gqlR = await client.mutate(`mutation($id: Int!) { eliminarAdicional(id: $id) { success message } }`, { id });
      const data = gqlR.eliminarAdicional;
      if (data.success) {
        success('Adicional eliminado');
        cargarAdicionales();
      } else {
        showError(data.message || 'Error al eliminar');
      }
    } catch (err) {
      showError('Error al eliminar adicional');
    }
  };

  // Generar PDF
  const descargarPDF = async (id) => {
    try {
      setGenerandoPDF(true);
      const gqlR = await client.query(`query($id: Int!) { adicionalDetalle(id: $id) { id consecutivo fecha descripcion tipo subtotal aiu_valor iva_valor valor_total valor_proyecto_anterior valor_proyecto_actualizado notas dias_adicionales actividades { codigo descripcion capitulo espacio unidad cantidad valor_unitario incremento factor_desperdicio factor_multiplicador valor_total } } }`, { id });
      if (!gqlR.adicionalDetalle) {
        showError('Error al cargar adicional');
        return;
      }

      // Cargar cronograma para obtener la fecha de entrega real (último hito)
      let fechaEntregaCronograma = null;
      try {
        const cronoR = await client.query(`query($pid: Int!) { cronogramaProyecto(proyecto_id: $pid) { cronograma { orden nombre fecha_fin es_hito } } }`, { pid: proyectoId });
        const items = cronoR.cronogramaProyecto?.cronograma || [];
        if (items.length > 0) {
          // Última fecha_fin disponible (orden mayor)
          const sorted = [...items].sort((a, b) => (b.orden || 0) - (a.orden || 0));
          const ultima = sorted.find(i => i.fecha_fin) || sorted[0];
          fechaEntregaCronograma = ultima?.fecha_fin || null;
        }
      } catch (e) { /* sin cronograma */ }

      const adicional = gqlR.adicionalDetalle;
      const esRetiro = adicional.tipo === 'retiro';
      setAdicionalPDF({
        ...adicional,
        nombre_cliente: proyecto.proyecto.nombre_cliente,
        cedula_cliente: proyecto.proyecto.cedula || '',
        telefono_cliente: proyecto.proyecto.telefono || '',
        correo_cliente: proyecto.proyecto.correo || '',
        proyecto_nombre: proyecto.proyecto.proyecto_nombre,
        area_m2: proyecto.proyecto.area_m2,
        apartamento: proyecto.proyecto.apartamento,
        fecha_entrega_actual: fechaEntregaCronograma
          || proyecto.proyecto.fecha_entrega_programada
          || proyecto.proyecto.fecha_entrega_estimada
          || null,
      });

      // Esperar render del PDF
      setTimeout(async () => {
        try {
          const element = pdfContainerRef.current?.querySelector('.adicional-page');
          if (!element) {
            showError('Error generando PDF');
            setGenerandoPDF(false);
            setAdicionalPDF(null);
            return;
          }

          const { default: jsPDF } = await import('jspdf');
          const { default: html2canvas } = await import('html2canvas');

          const canvas = await html2canvas(element, {
            scale: 2, useCORS: true, allowTaint: true,
            backgroundColor: '#ffffff', imageTimeout: 15000
          });

          const imgData = canvas.toDataURL('image/jpeg', 0.98);
          const pxW = canvas.width;
          const pxH = canvas.height;
          const pdfW = 612;
          const pdfH = (pxH * pdfW) / pxW;

          const pdf = new jsPDF({ unit: 'pt', format: [pdfW, pdfH], orientation: 'portrait' });
          pdf.addImage(imgData, 'JPEG', 0, 0, pdfW, pdfH);

          const clienteNombre = proyecto.proyecto.nombre_cliente.replace(/\s+/g, '-');
          const proyectoNombre = proyecto.proyecto.proyecto_nombre.replace(/\s+/g, '-');
          const prefijo = esRetiro ? 'RETIRO' : 'ADICIONAL';
          pdf.save(`${prefijo}-${adicional.consecutivo}-${clienteNombre}-${proyectoNombre}.pdf`);

          success('PDF descargado');
        } catch (err) {
          showError('Error generando PDF');
          console.error(err);
        } finally {
          setGenerandoPDF(false);
          setAdicionalPDF(null);
        }
      }, 1500);

    } catch (err) {
      showError('Error al descargar PDF');
      setGenerandoPDF(false);
    }
  };

  const resetFormulario = () => {
    setEditandoId(null);
    setTipoAdicional('adicional');
    setDescripcion('');
    setDiasAdicionales(0);
    setIncrementoGlobal(0);
    prevIncGlobalRef.current = 0;
    setNotas('');
    setActividadesSeleccionadas([]);
    setCategoriaActiva('');
    setEspacioActivo('');
    setBuscarActividad('');
    setBuscarRetiro('');
    setActividadesProyecto([]);
  };

  const nuevoAdicional = () => {
    resetFormulario();
    setVista('formulario');
  };

  const cancelarFormulario = () => {
    resetFormulario();
    setVista('lista');
  };

  const getEstadoBadge = (estado) => {
    const estilos = {
      borrador: { bg: '#6b7280', label: 'Borrador' },
      enviado: { bg: '#3b82f6', label: 'Enviado' },
      aprobado: { bg: '#10b981', label: 'Aprobado' },
      rechazado: { bg: '#ef4444', label: 'Rechazado' }
    };
    const e = estilos[estado] || estilos.borrador;
    return <span className="adicional-estado-badge" style={{ backgroundColor: e.bg }}>{e.label}</span>;
  };

  const getTipoBadge = (tipo) => {
    if (tipo === 'retiro') {
      return <span className="adicional-tipo-badge retiro">Retiro</span>;
    }
    return <span className="adicional-tipo-badge adicional">Adicional</span>;
  };

  // Agrupar actividades seleccionadas por espacio
  const actividadesPorEspacio = actividadesSeleccionadas.reduce((acc, act) => {
    const esp = act.espacio || 'GENERALES';
    if (!acc[esp]) acc[esp] = [];
    acc[esp].push(act);
    return acc;
  }, {});

  // ========== RENDER ==========

  if (loading) {
    return <div className="adicionales-loading">Cargando adicionales...</div>;
  }

  // --- VISTA HISTORIAL ---
  if (vista === 'historial') {
    return (
      <div className="adicionales-container">
        <div className="adicionales-header">
          <h2>Historial de Adicionales y Retiros Aprobados</h2>
          <button className="btn-cancelar-adicional" onClick={() => setVista('lista')}>Volver</button>
        </div>

        {loadingHistorial ? (
          <div className="adicionales-loading">Cargando historial...</div>
        ) : historial.length === 0 ? (
          <div className="adicionales-vacio">
            <p>No hay adicionales aprobados en este proyecto.</p>
          </div>
        ) : (
          <table className="adicionales-tabla historial-tabla">
            <thead>
              <tr>
                <th>#</th>
                <th>Tipo</th>
                <th>Descripcion</th>
                <th>Valor</th>
                <th>Valor Proyecto Antes</th>
                <th>Valor Proyecto Despues</th>
                <th>Fecha Aprobacion</th>
                <th>Comprobante</th>
              </tr>
            </thead>
            <tbody>
              {historial.map(h => (
                <tr key={h.id}>
                  <td>{h.consecutivo}</td>
                  <td>{getTipoBadge(h.tipo)}</td>
                  <td>{h.descripcion || '-'}</td>
                  <td className="valor-col" style={h.tipo === 'retiro' ? { color: '#ef4444' } : {}}>
                    {h.tipo === 'retiro' ? '-' : ''}{formatCurrency(h.valor_adicional)}
                  </td>
                  <td className="valor-col">{formatCurrency(h.valor_proyecto_antes)}</td>
                  <td className="valor-col valor-despues">{formatCurrency(h.valor_proyecto_despues)}</td>
                  <td>{h.fecha_aprobacion ? new Date(h.fecha_aprobacion).toLocaleDateString('es-CO') : '-'}</td>
                  <td>
                    {h.comprobante_archivo && (
                      <a
                        href={comprobanteUrl(h.comprobante_archivo)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-accion btn-pdf"
                      >
                        Ver
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  // --- VISTA LISTA ---
  if (vista === 'lista') {
    return (
      <div className="adicionales-container">
        <div className="adicionales-header">
          <h2>Adicionales y Retiros</h2>
          <div className="adicionales-header-actions">
            {adicionales.some(a => a.estado === 'aprobado') && (
              <button className="btn-historial-adicional" onClick={verHistorial}>Historial</button>
            )}
            <button className="btn-nuevo-adicional" onClick={() => { resetFormulario(); setTipoAdicional('adicional'); setVista('formulario'); }}>+ Adicional</button>
            <button className="btn-nuevo-retiro" onClick={() => { resetFormulario(); setTipoAdicional('retiro'); setVista('formulario'); }}>- Retiro</button>
          </div>
        </div>

        {adicionales.length === 0 ? (
          <div className="adicionales-vacio">
            <p>No hay adicionales ni retiros creados para este proyecto.</p>
            <p>Los adicionales agregan actividades extra. Los retiros eliminan actividades del alcance original.</p>
          </div>
        ) : (
          <table className="adicionales-tabla">
            <thead>
              <tr>
                <th>#</th>
                <th>Tipo</th>
                <th>Fecha</th>
                <th>Descripcion</th>
                <th>Valor Total</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {adicionales.map(adic => (
                <tr key={adic.id} className={adic.estado === 'aprobado' ? 'fila-aprobada' : ''}>
                  <td>{adic.consecutivo}</td>
                  <td>{getTipoBadge(adic.tipo)}</td>
                  <td>{adic.fecha}</td>
                  <td>{adic.descripcion || '-'}</td>
                  <td className="valor-col" style={adic.tipo === 'retiro' ? { color: '#ef4444' } : {}}>
                    {adic.tipo === 'retiro' ? '-' : ''}{formatCurrency(adic.valor_total)}
                  </td>
                  <td>{getEstadoBadge(adic.estado)}</td>
                  <td className="acciones-col">
                    {adic.estado !== 'aprobado' ? (
                      <>
                        <button className="btn-accion btn-aprobar" onClick={() => abrirModalAprobacion(adic)} title="Aprobar">Aprobar</button>
                        <button className="btn-accion btn-editar" onClick={() => editarAdicional(adic.id)} title="Editar">Editar</button>
                        <button className="btn-accion btn-pdf" onClick={() => descargarPDF(adic.id)} disabled={generandoPDF} title="Descargar PDF">
                          {generandoPDF ? '...' : 'PDF'}
                        </button>
                        <button className="btn-accion btn-eliminar" onClick={() => eliminarAdicional(adic.id)} title="Eliminar">Eliminar</button>
                      </>
                    ) : (
                      <>
                        <button className="btn-accion btn-pdf" onClick={() => descargarPDF(adic.id)} disabled={generandoPDF} title="Descargar PDF">
                          {generandoPDF ? '...' : 'PDF'}
                        </button>
                        {adic.comprobante_archivo && (
                          <a
                            href={comprobanteUrl(adic.comprobante_archivo)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-accion btn-comprobante"
                            title="Ver comprobante"
                          >
                            Comprobante
                          </a>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Modal de aprobacion */}
        {modalAprobacion && (
          <div className="modal-overlay" onClick={() => { setModalAprobacion(null); setArchivoComprobante(null); }}>
            <div className="modal-aprobacion" onClick={(e) => e.stopPropagation()}>
              <h3>Aprobar {modalAprobacion.tipo === 'retiro' ? 'Retiro' : 'Adicional'} #{modalAprobacion.consecutivo}</h3>

              <div className="aprobacion-resumen">
                <div className="aprobacion-item">
                  <span className="aprobacion-label">Descripcion:</span>
                  <span>{modalAprobacion.descripcion || '-'}</span>
                </div>
                <div className="aprobacion-item">
                  <span className="aprobacion-label">Valor del {modalAprobacion.tipo === 'retiro' ? 'Retiro' : 'Adicional'}:</span>
                  <span className="aprobacion-valor" style={modalAprobacion.tipo === 'retiro' ? { color: '#ef4444' } : {}}>
                    {modalAprobacion.tipo === 'retiro' ? '-' : ''}{formatCurrency(modalAprobacion.valor_total)}
                  </span>
                </div>
                <div className="aprobacion-item">
                  <span className="aprobacion-label">Valor Actual del Proyecto:</span>
                  <span>{formatCurrency(proyecto?.proyecto?.valor_final)}</span>
                </div>
                <div className="aprobacion-item destacado">
                  <span className="aprobacion-label">Nuevo Valor del Proyecto:</span>
                  <span className={`aprobacion-valor ${modalAprobacion.tipo === 'retiro' ? 'rojo' : 'verde'}`}>
                    {formatCurrency(
                      modalAprobacion.tipo === 'retiro'
                        ? (parseFloat(proyecto?.proyecto?.valor_final) || 0) - (parseFloat(modalAprobacion.valor_total) || 0)
                        : (parseFloat(proyecto?.proyecto?.valor_final) || 0) + (parseFloat(modalAprobacion.valor_total) || 0)
                    )}
                  </span>
                </div>
              </div>

              <div className="aprobacion-archivo">
                <label>Comprobante de Aprobacion (obligatorio)</label>
                <p className="aprobacion-archivo-hint">Adjunte el documento firmado o comprobante que autoriza este adicional.</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
                  onChange={(e) => setArchivoComprobante(e.target.files[0] || null)}
                />
                {archivoComprobante && (
                  <div className="archivo-seleccionado">
                    {archivoComprobante.name} ({(archivoComprobante.size / 1024).toFixed(0)} KB)
                  </div>
                )}
              </div>

              <div className="modal-aprobacion-actions">
                <button className="btn-cancelar-adicional" onClick={() => { setModalAprobacion(null); setArchivoComprobante(null); }}>
                  Cancelar
                </button>
                <button
                  className="btn-confirmar-aprobacion"
                  onClick={aprobarAdicional}
                  disabled={!archivoComprobante || aprobando}
                >
                  {aprobando ? 'Aprobando...' : 'Confirmar Aprobacion'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* PDF off-screen render */}
        <div ref={pdfContainerRef} style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          {adicionalPDF && <AdicionalPDF adicional={adicionalPDF} />}
        </div>
      </div>
    );
  }

  // --- VISTA FORMULARIO ---
  return (
    <div className="adicionales-container">
      <div className="adicionales-header">
        <h2>{editandoId ? 'Editar' : 'Nuevo'} {tipoAdicional === 'retiro' ? 'Retiro' : 'Adicional'}</h2>
        <button className="btn-cancelar-adicional" onClick={cancelarFormulario}>Cancelar</button>
      </div>

      {/* Tipo selector */}
      <div className="adicional-tipo-selector">
        <button
          className={`tipo-btn ${tipoAdicional === 'adicional' ? 'activo adicional' : ''}`}
          onClick={() => setTipoAdicional('adicional')}
        >
          + Adicional
        </button>
        <button
          className={`tipo-btn ${tipoAdicional === 'retiro' ? 'activo retiro' : ''}`}
          onClick={() => setTipoAdicional('retiro')}
        >
          - Retiro
        </button>
      </div>

      {/* Datos basicos */}
      <div className="adicional-form-section">
        <div className="adicional-form-row">
          <div className="adicional-form-field" style={{ flex: 2 }}>
            <label>Descripcion del {tipoAdicional === 'retiro' ? 'retiro' : 'adicional'}</label>
            <input
              type="text"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder={tipoAdicional === 'retiro' ? 'Ej: Retiro de actividades baño' : 'Ej: Adicional closet principal'}
            />
          </div>
          <div className="adicional-form-field" style={{ flex: 0.5 }}>
            <label>{tipoAdicional === 'retiro' ? 'Días que resta al cronograma' : 'Días hábiles adicionales al cronograma'}</label>
            <input type="number" min="0" value={diasAdicionales}
              onChange={(e) => setDiasAdicionales(e.target.value)}
              placeholder="0" />
          </div>
          {/* El incremento de actividades solo aplica a adicionales (no a retiros) */}
          {tipoAdicional === 'adicional' && (
            <div className="adicional-form-field" style={{ flex: 0.5 }}>
              <label>Incremento Actividades (%)</label>
              <input type="number" step="any" value={incrementoGlobal}
                onChange={(e) => setIncrementoGlobal(e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)}
                placeholder="0" />
            </div>
          )}
          <div className="adicional-form-field" style={{ flex: 1 }}>
            <label>Notas</label>
            <textarea
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              placeholder="Observaciones..."
              rows={1}
            />
          </div>
        </div>
      </div>

      {/* Selector de actividades */}
      <div className="adicional-form-section">
        <h3>{tipoAdicional === 'retiro' ? 'Seleccionar Actividades a Retirar del Proyecto' : 'Seleccionar Actividades'}</h3>

        {tipoAdicional === 'retiro' ? (
          /* ===== SELECTOR RETIRO: actividades del proyecto ===== */
          <div className="actividades-selector">
            <div className="selector-controles">
              <div className="selector-nivel selector-buscar" style={{ flex: 1 }}>
                <label>Buscar actividad del proyecto</label>
                <input
                  type="text"
                  placeholder="Filtrar actividades del proyecto..."
                  value={buscarRetiro}
                  onChange={(e) => setBuscarRetiro(e.target.value)}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button
                  className="btn-agregar-actividad"
                  onClick={() => cargarActividadesProyecto(buscarRetiro)}
                  style={{ padding: '0.5rem 1rem' }}
                >
                  Buscar
                </button>
              </div>
            </div>

            <div className="actividades-disponibles" style={{ marginTop: '10px' }}>
              {loadingProyecto ? (
                <div className="loading-small">Cargando actividades del proyecto...</div>
              ) : actividadesProyecto.length === 0 ? (
                <div className="sin-actividades">
                  {buscarRetiro ? 'No se encontraron actividades' : 'Haga clic en "Buscar" para cargar las actividades del proyecto'}
                </div>
              ) : (
                <table className="tabla-actividades-disponibles">
                  <thead>
                    <tr>
                      <th>Capitulo</th>
                      <th>Actividad</th>
                      <th>Valor</th>
                      <th>Accion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {actividadesProyecto
                      .filter(act => !actividadesSeleccionadas.some(s => s.actividad_id === act.actividad_id))
                      .map(act => (
                        <tr key={act.actividad_id}>
                          <td style={{ fontSize: '0.8rem', color: '#666' }}>{act.capitulo}</td>
                          <td>{act.descripcion}</td>
                          <td className="valor-final-celda">{formatCurrency(act.valor_total)}</td>
                          <td>
                            <button className="btn-agregar-actividad retiro" onClick={() => agregarActividadRetiro(act)}>
                              - Retirar
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ) : (
          /* ===== SELECTOR ADICIONAL: desde matriz ===== */
          <div className="actividades-selector">
            <div className="selector-controles selector-3-niveles">
              <div className="selector-nivel">
                <label>Categoria</label>
                <select value={categoriaActiva} onChange={(e) => setCategoriaActiva(e.target.value)}>
                  <option value="">Seleccionar categoria</option>
                  {ORDEN_CATEGORIAS.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>

              {categoriaActiva && (
                <div className="selector-nivel">
                  <label>Espacio</label>
                  <select value={espacioActivo} onChange={(e) => { setEspacioActivo(e.target.value); setBuscarActividad(''); }}>
                    <option value="">Seleccionar espacio</option>
                    {espaciosDisponibles.map(esp => <option key={esp} value={esp}>{esp}</option>)}
                  </select>
                </div>
              )}

              {categoriaActiva && espacioActivo && (
                <div className="selector-nivel selector-buscar">
                  <label>Buscar</label>
                  <input
                    type="text"
                    placeholder="Filtrar actividades..."
                    value={buscarActividad}
                    onChange={(e) => setBuscarActividad(e.target.value)}
                  />
                </div>
              )}
            </div>

            {categoriaActiva && espacioActivo && (
              <div className="actividades-disponibles">
                {loadingActividades ? (
                  <div className="loading-small">Cargando actividades...</div>
                ) : actividadesDisponibles.length === 0 ? (
                  <div className="sin-actividades">No se encontraron actividades</div>
                ) : (
                  <table className="tabla-actividades-disponibles">
                    <thead>
                      <tr>
                        <th>Codigo</th>
                        <th>Actividad</th>
                        <th>Unidad</th>
                        <th>Valor Final</th>
                        <th>Accion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {actividadesDisponibles.map(act => {
                        const vf = calcularValorFinalActividad(act);
                        return (
                          <tr key={act.id}>
                            <td>{act.codigo}</td>
                            <td>{act.descripcion || act.actividad}</td>
                            <td>{act.unidad}</td>
                            <td className="valor-final-celda">
                              {vf > 0 ? `$${Math.floor(vf).toLocaleString('es-CO')}` : '-'}
                            </td>
                            <td>
                              <button className="btn-agregar-actividad" onClick={() => agregarActividad(act)}>
                                + Agregar
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Actividades seleccionadas */}
      <div className="adicional-form-section">
        <h3>Actividades Agregadas ({actividadesSeleccionadas.length})</h3>

        {actividadesSeleccionadas.length === 0 ? (
          <div className="sin-actividades">
            No hay actividades agregadas. Seleccione una categoria y agregue actividades.
          </div>
        ) : (
          Object.entries(actividadesPorEspacio).map(([espacio, actividades]) => (
            <div key={espacio} className="cap-acts-agregadas">
              <h5>{espacio}</h5>
              <table>
                <thead>
                  <tr>
                    <th>Codigo</th>
                    <th>Descripcion</th>
                    <th>Unidad</th>
                    {tipoAdicional !== 'retiro' && <th>Cantidad</th>}
                    {tipoAdicional !== 'retiro' && <th>Valor Unit.</th>}
                    <th>Valor Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {actividades.map((act) => {
                    const globalIndex = actividadesSeleccionadas.findIndex(
                      a => a.actividad_id === act.actividad_id
                    );
                    return (
                      <tr key={act.actividad_id}>
                        <td>{act.codigo}</td>
                        <td>{act.descripcion}</td>
                        <td>{act.unidad}</td>
                        {tipoAdicional !== 'retiro' && (
                          <td>
                            <input
                              type="number"
                              value={act.cantidad}
                              onChange={(e) => actualizarActividad(globalIndex, 'cantidad', e.target.value)}
                              min="0"
                              step="0.01"
                              className="input-cantidad"
                            />
                          </td>
                        )}
                        {tipoAdicional !== 'retiro' && (
                          <td>
                            <input
                              type="number"
                              value={act.valor_unitario}
                              onChange={(e) => actualizarActividad(globalIndex, 'valor_unitario', e.target.value)}
                              min="0"
                              className="input-valor"
                            />
                          </td>
                        )}
                        <td className="valor-total">{formatCurrency(act.valor_total)}</td>
                        <td>
                          <button className="btn-eliminar-actividad" onClick={() => eliminarActividad(globalIndex)} title="Eliminar">
                            X
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))
        )}
      </div>

      {/* Resumen financiero */}
      {actividadesSeleccionadas.length > 0 && (
        <div className="adicional-form-section adicional-resumen">
          <h3>Resumen Financiero</h3>
          <div className="adicional-totales-grid">
            <div className="adicional-total-item">
              <span className="total-label">Subtotal</span>
              <span className="total-valor">{formatCurrency(totales.subtotal)}</span>
            </div>
            <div className="adicional-total-item">
              <span className="total-label">AIU (15%)</span>
              <span className="total-valor">{formatCurrency(totales.aiu_valor)}</span>
            </div>
            <div className="adicional-total-item">
              <span className="total-label">IVA</span>
              <span className="total-valor">{formatCurrency(totales.iva_valor)}</span>
            </div>
            <div className="adicional-total-item destacado">
              <span className="total-label">Valor Total {tipoAdicional === 'retiro' ? 'Retiro' : 'Adicional'}</span>
              <span className="total-valor" style={tipoAdicional === 'retiro' ? { color: '#ef4444' } : {}}>
                {tipoAdicional === 'retiro' ? '-' : ''}{formatCurrency(totales.valor_total)}
              </span>
            </div>
            <div className="adicional-separador"></div>
            <div className="adicional-total-item">
              <span className="total-label">Valor Anterior del Proyecto</span>
              <span className="total-valor">{formatCurrency(totales.valor_proyecto_anterior)}</span>
            </div>
            <div className={`adicional-total-item destacado ${tipoAdicional === 'retiro' ? 'rojo' : 'verde'}`}>
              <span className="total-label">Valor Actualizado del Proyecto</span>
              <span className="total-valor">{formatCurrency(totales.valor_proyecto_actualizado)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Boton guardar */}
      <div className="adicional-form-actions">
        <button className="btn-cancelar-adicional" onClick={cancelarFormulario}>Cancelar</button>
        <button className={`btn-guardar-adicional ${tipoAdicional === 'retiro' ? 'retiro' : ''}`} onClick={guardarAdicional} disabled={guardando || actividadesSeleccionadas.length === 0}>
          {guardando ? 'Guardando...' : (editandoId
            ? `Actualizar ${tipoAdicional === 'retiro' ? 'Retiro' : 'Adicional'}`
            : `Guardar ${tipoAdicional === 'retiro' ? 'Retiro' : 'Adicional'}`
          )}
        </button>
      </div>

      {/* PDF off-screen render */}
      <div ref={pdfContainerRef} style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        {adicionalPDF && <AdicionalPDF adicional={adicionalPDF} />}
      </div>
    </div>
  );
};

export default AdicionalesProyecto;
